/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Implements secure token handling and session management.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, clearCsrfToken } from '../services/api';
import { UserSchema, LoginResponseSchema, RegisterResponseSchema } from '../schemas';
import type { User, LoginResponse, RegisterResponse } from '../schemas';
import config from '../config/environment';
import {
  cognitoLogin as cognitoAuthenticate,
  cognitoLogout as cognitoSignOut,
  cognitoChangePassword as cognitoChangePass,
} from '../services/cognito';
import { storageGet, storageSet, storageRemove } from '../services/storage';
import { clearSessionMarker, markSessionActive } from '../services/session';
import { logoutOnServer } from '../services/api/auth';

// Auth state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  selectedOrganisationId: number | null;
}

// Auth context interface
interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  selectOrganisation: (organisationId: number) => void;
  refreshUser: () => Promise<void>;
  getSelectedOrganisation: () => { id: number; name: string; role: string } | null;
  hasMultipleOrganisations: () => boolean;
  changePassword: (oldPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
  updateProfile: (data: { firstName: string; lastName: string; jobTitle: string }) => Promise<void>;
}

// Registration data
interface RegisterData {
  email: string;
  password: string;
  passwordConfirm: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  organisationName?: string;
  organisationCountryId?: number;
  invitationToken?: string;
  /** When set, registers the user as a contributor against an existing
   *  organisation via /auth/register-for-org/ instead of creating a new org. */
  existingOrganisationId?: number;
  /** Optional. When provided alongside existingOrganisationId, also files a
   *  pending OrganisationClaim requesting editor access. */
  claimReason?: string;
  /** When set, activates a pre-created inactive account via a signed
   *  admin-issued link. Identity + organisation come from the token
   *  server-side, so no client-supplied org id is trusted. */
  activationToken?: string;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session storage keys (more secure than localStorage for session data)
const SESSION_USER_KEY = 'econori_user';
const SESSION_ORG_KEY = 'econori_selected_org';

// Custom events for cross-component communication
export const AUTH_EVENTS = {
  USER_CHANGED: 'userAuthChanged',
  ORGANISATION_CHANGED: 'organisationChanged',
  SESSION_EXPIRED: 'sessionExpired',
} as const;

/**
 * Emit auth event for cross-component communication
 */
function emitAuthEvent(eventName: string, detail?: unknown): void {
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}

/**
 * Auth Provider Component
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    selectedOrganisationId: null,
  });

  // Session timeout refs
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Clear session timeouts
   */
  const clearSessionTimeouts = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  /**
   * Set up session timeout
   */
  const setupSessionTimeout = useCallback(() => {
    clearSessionTimeouts();

    // Warning before session expires
    warningTimeoutRef.current = setTimeout(() => {
      emitAuthEvent(AUTH_EVENTS.SESSION_EXPIRED, { warning: true });
    }, config.sessionTimeout - config.sessionWarningBefore);

    // Session expiry
    sessionTimeoutRef.current = setTimeout(() => {
      emitAuthEvent(AUTH_EVENTS.SESSION_EXPIRED, { expired: true });
      // Auto-logout on session expiry
      logout();
    }, config.sessionTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSessionTimeouts]);

  /**
   * Reset session timeout on user activity
   */
  const resetSessionTimeout = useCallback(() => {
    if (state.isAuthenticated) {
      setupSessionTimeout();
    }
  }, [state.isAuthenticated, setupSessionTimeout]);

  /**
   * Load user from storage on mount
   */
  useEffect(() => {
    const loadUser = () => {
      try {
        // Try session-scoped storage first, then local-scoped fallback.
        let userDataStr = storageGet(SESSION_USER_KEY, 'session');
        if (!userDataStr) {
          userDataStr = storageGet('userData', 'local');
        }

        const storedOrgId = storageGet(SESSION_ORG_KEY, 'session') ||
                           storageGet('selectedOrganisationId', 'local');

        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          const validatedUser = UserSchema.safeParse(userData);

          if (validatedUser.success) {
            let selectedOrgId: number | null = null;

            if (validatedUser.data.organisations && validatedUser.data.organisations.length > 0) {
              if (storedOrgId) {
                const orgExists = validatedUser.data.organisations.some(
                  (org) => org.id === Number(storedOrgId)
                );
                selectedOrgId = orgExists
                  ? Number(storedOrgId)
                  : validatedUser.data.organisations[0].id;
              } else {
                selectedOrgId = validatedUser.data.organisations[0].id;
              }
            }

            setState({
              user: validatedUser.data,
              isAuthenticated: true,
              isLoading: false,
              selectedOrganisationId: selectedOrgId,
            });

            setupSessionTimeout();
            return;
          }
        }

        // No valid user found
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          selectedOrganisationId: null,
        });
      } catch (err) {
        console.error('Error loading user from storage:', err);
        clearStorage();
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          selectedOrganisationId: null,
        });
      }
    };

    loadUser();

    // Listen for storage changes (cross-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userData') {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [setupSessionTimeout]);

  /**
   * Set up activity listeners for session management
   */
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      resetSessionTimeout();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearSessionTimeouts();
    };
  }, [state.isAuthenticated, resetSessionTimeout, clearSessionTimeouts]);

  /**
   * Listen for session expiry from authFetch (refresh token failure).
   * When the refresh token is invalid, authFetch dispatches 'sessionExpired'
   * so the user is properly logged out instead of seeing "Permissions not
   * resolved" on every subsequent API call.
   */
  useEffect(() => {
    const handleTokenExpiry = (event: Event) => {
      // SESSION_EXPIRED fires for two distinct reasons:
      //   { warning: true } — the pre-expiry countdown, consumed by the
      //                       SessionTimeout modal to offer "Continue Session".
      //                       This is only a heads-up and must NOT log out.
      //   { expired: true } — the real timeout, or authFetch signalling a
      //                       refresh-token failure. This should log out.
      // Without this guard the warning event logged the user out minutes
      // before their session actually expired (and before the modal could show).
      const detail = (event as CustomEvent<{ warning?: boolean; expired?: boolean }>).detail;
      if (detail?.warning) {
        return;
      }

      // Only act if we think we're still authenticated
      if (state.isAuthenticated) {
        clearSessionTimeouts();
        clearStorage();
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          selectedOrganisationId: null,
        });
        // Note: do NOT re-emit SESSION_EXPIRED here — this handler IS the
        // listener for that event, so re-emitting would cause infinite recursion.
      }
    };

    window.addEventListener('sessionExpired', handleTokenExpiry as EventListener);
    return () => window.removeEventListener('sessionExpired', handleTokenExpiry as EventListener);
  }, [state.isAuthenticated, clearSessionTimeouts]);

  /**
   * Clear all storage
   */
  const clearStorage = () => {
    storageRemove(SESSION_USER_KEY, 'session');
    storageRemove(SESSION_ORG_KEY, 'session');
    storageRemove('userId', 'local');
    storageRemove('userData', 'local');
    storageRemove('selectedOrganisationId', 'local');
    storageRemove('access_token', 'local');
    clearSessionMarker();
    clearCsrfToken();
  };

  /**
   * Save user to storage
   *
   * Uses sessionStorage as primary for better security (cleared on tab close).
   * Also mirrors to localStorage for cross-tab awareness and backward compatibility.
   */
  const saveUserToStorage = (user: User, organisationId: number | null) => {
    // Use sessionStorage for better security (cleared on tab close)
    storageSet(SESSION_USER_KEY, JSON.stringify(user), 'session');
    if (organisationId) {
      storageSet(SESSION_ORG_KEY, String(organisationId), 'session');
    }
    // Also save to local storage for cross-tab awareness (backward compatibility)
    storageSet('userId', String(user.id), 'local');
    storageSet('userData', JSON.stringify(user), 'local');
    if (organisationId) {
      storageSet('selectedOrganisationId', String(organisationId), 'local');
    }
  };

  /**
   * Login
   *
   * In Cognito mode we try the SRP flow first. If Cognito rejects the
   * credentials (e.g. the user's Cognito record is missing or out of
   * sync because a previous register/reset was rejected by the password
   * policy), we transparently fall back to Django's local
   * email/password endpoint. On successful local auth the backend
   * attempts to reconcile Cognito so the next login uses SRP again.
   */
  const login = async (email: string, password: string): Promise<void> => {
    let response: LoginResponse;
    const normalisedEmail = email.trim().toLowerCase();

    if (config.authMode === 'cognito') {
      try {
        const idToken = await cognitoAuthenticate(email, password);
        response = await apiClient.post<LoginResponse>(
          '/api/users/cognito/login/',
          { id_token: idToken }
        );
      } catch (cognitoErr) {
        // Fall back to local Django auth. If that also fails, surface
        // the *local* error — it speaks to the same credential pair the
        // user just entered, and the original Cognito error is usually
        // less specific (e.g. UserNotFoundException leaks nothing
        // useful with prevent_user_existence_errors enabled).
        try {
          response = await apiClient.post<LoginResponse>(
            '/api/users/login/',
            { email: normalisedEmail, password }
          );
        } catch {
          throw cognitoErr instanceof Error
            ? cognitoErr
            : new Error('Login failed');
        }
      }
    } else {
      response = await apiClient.post<LoginResponse>(
        '/api/users/login/',
        { email: normalisedEmail, password }
      );
    }

    const validatedResponse = LoginResponseSchema.safeParse(response);
    if (!validatedResponse.success) {
      throw new Error('Invalid response from server');
    }

    const userData: User = {
      id: validatedResponse.data.id,
      email: validatedResponse.data.email,
      first_name: validatedResponse.data.first_name,
      last_name: validatedResponse.data.last_name,
      job_title: validatedResponse.data.job_title,
      email_verified: validatedResponse.data.email_verified,
      organisations: validatedResponse.data.organisations || [],
      is_admin: validatedResponse.data.is_admin || false,
      needs_password_upgrade:
        validatedResponse.data.needs_password_upgrade || false,
    };

    const selectedOrgId = userData.organisations && userData.organisations.length > 0
      ? userData.organisations[0].id
      : null;

    // Store JWT tokens for stateless API auth (needed when session
    // cookies can't be shared across origins, e.g. HTTP frontend → HTTPS API)
    const accessToken = validatedResponse.data.access_token || validatedResponse.data.token;
    if (accessToken) {
      storageSet('access_token', accessToken, 'local');
    }
    // The refresh token is delivered as an HttpOnly cookie and is
    // deliberately not stored here — that is the point of #386. All we keep
    // is a marker saying a session exists.
    markSessionActive();

    saveUserToStorage(userData, selectedOrgId);

    setState({
      user: userData,
      isAuthenticated: true,
      isLoading: false,
      selectedOrganisationId: selectedOrgId,
    });

    setupSessionTimeout();
    emitAuthEvent(AUTH_EVENTS.USER_CHANGED, { userId: userData.id });
  };

  /**
   * Register
   */
  const register = async (data: RegisterData): Promise<void> => {
    const payload: Record<string, unknown> = {
      email: data.email.trim().toLowerCase(),
      password: data.password,
      password_confirm: data.passwordConfirm,
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      job_title: (data.jobTitle || '').trim(),
    };

    let endpoint = '/api/users/register/';

    if (data.activationToken) {
      // Activation path: the signed token pins identity + org to the exact
      // inactive account it was minted for. The server ignores any
      // client-supplied email / organisation id.
      endpoint = '/api/users/register-for-org/';
      payload.activation_token = data.activationToken;
    } else if (data.existingOrganisationId) {
      // Contributor path: join an existing org as read-only and optionally
      // file a pending claim. No new org is created.
      endpoint = '/api/users/register-for-org/';
      payload.organisation_id = data.existingOrganisationId;
      if (data.claimReason && data.claimReason.trim()) {
        payload.claim_reason = data.claimReason.trim();
      }
    } else if (data.invitationToken) {
      payload.invitation_token = data.invitationToken;
    } else {
      payload.organisation_name = (data.organisationName || '').trim();
      payload.organisation_registered_country_id = data.organisationCountryId;
    }

    const response = await apiClient.post<RegisterResponse>(endpoint, payload);

    const validatedResponse = RegisterResponseSchema.safeParse(response);
    if (!validatedResponse.success) {
      throw new Error('Invalid response from server');
    }

    // Build organisations array: prefer the `organisations` list from the
    // response (new backend), fall back to constructing from organisation_id
    // (old backend / backward compatibility).
    const orgRole = validatedResponse.data.invitation_accepted ? 'member' : 'admin';
    const organisations = validatedResponse.data.organisations && validatedResponse.data.organisations.length > 0
      ? validatedResponse.data.organisations
      : validatedResponse.data.organisation_id
        ? [{
            id: validatedResponse.data.organisation_id,
            name: validatedResponse.data.organisation_name || '',
            role: orgRole,
          }]
        : [];

    const userData: User = {
      id: validatedResponse.data.id,
      email: validatedResponse.data.email,
      first_name: validatedResponse.data.first_name,
      last_name: validatedResponse.data.last_name,
      job_title: validatedResponse.data.job_title,
      email_verified: validatedResponse.data.email_verified,
      organisations,
      is_admin: validatedResponse.data.is_admin || false,
      needs_password_upgrade:
        validatedResponse.data.needs_password_upgrade || false,
    };

    const selectedOrgId = organisations.length > 0
      ? organisations[0].id
      : null;

    // Store JWT tokens for stateless API auth (same as login)
    const accessToken = validatedResponse.data.access_token || validatedResponse.data.token;
    if (accessToken) {
      storageSet('access_token', accessToken, 'local');
    }
    // The refresh token is delivered as an HttpOnly cookie and is
    // deliberately not stored here — that is the point of #386. All we keep
    // is a marker saying a session exists.
    markSessionActive();

    saveUserToStorage(userData, selectedOrgId);

    setState({
      user: userData,
      isAuthenticated: true,
      isLoading: false,
      selectedOrganisationId: selectedOrgId,
    });

    setupSessionTimeout();
    emitAuthEvent(AUTH_EVENTS.USER_CHANGED, { userId: userData.id });
  };

  /**
   * Logout
   */
  const logout = useCallback(() => {
    if (config.authMode === 'cognito') {
      // Fire-and-forget, like logoutOnServer below: cognitoSignOut now awaits
      // the dynamically imported SDK, and local state is cleared either way.
      void cognitoSignOut();
    }

    // Ask the backend to blacklist the refresh token and clear its cookie.
    // Fire-and-forget: local state is cleared either way, so a network
    // failure cannot leave the user stuck looking logged in.
    void logoutOnServer();

    clearSessionTimeouts();
    clearStorage();

    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      selectedOrganisationId: null,
    });

    // Emit storage event for cross-tab logout
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'userData',
        newValue: null,
      })
    );

    emitAuthEvent(AUTH_EVENTS.USER_CHANGED, { userId: null });
  }, [clearSessionTimeouts]);

  /**
   * Select organisation
   */
  const selectOrganisation = (organisationId: number) => {
    // Both early-returns log a warning so a silent state/UI divergence
    // doesn't slip through unnoticed — picking an org that isn't in the
    // user's memberships list (e.g. a new membership that hasn't refreshed
    // into the auth payload yet) used to fail silently while the dropdown
    // visually flicker-snapped back to the prior value, leaving fetches
    // pointed at the wrong org.
    if (!state.user?.organisations) {
      console.warn(
        '[AuthContext] selectOrganisation called before user.organisations loaded',
        { organisationId },
      );
      return;
    }

    const org = state.user.organisations.find((o) => o.id === organisationId);
    if (!org) {
      console.warn(
        '[AuthContext] selectOrganisation: org id not in user memberships, ignoring',
        {
          organisationId,
          availableOrgIds: state.user.organisations.map((o) => o.id),
        },
      );
      return;
    }

    storageSet(SESSION_ORG_KEY, String(organisationId), 'session');
    storageSet('selectedOrganisationId', String(organisationId), 'local');

    setState((prev) => ({
      ...prev,
      selectedOrganisationId: organisationId,
    }));

    // Emit events for cross-component sync
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'selectedOrganisationId',
        newValue: String(organisationId),
      })
    );
    emitAuthEvent(AUTH_EVENTS.ORGANISATION_CHANGED, { organisationId });
  };

  /**
   * Refresh user data
   */
  const refreshUser = async (): Promise<void> => {
    // This would typically call an API endpoint to get fresh user data
    // For now, just reload from storage
    const userDataStr = storageGet(SESSION_USER_KEY, 'session') ||
                        storageGet('userData', 'local');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      const validatedUser = UserSchema.safeParse(userData);
      if (validatedUser.success) {
        setState((prev) => ({
          ...prev,
          user: validatedUser.data,
        }));
      }
    }
  };

  /**
   * Get selected organisation
   */
  const getSelectedOrganisation = (): { id: number; name: string; role: string } | null => {
    if (!state.user?.organisations || !state.selectedOrganisationId) {
      return null;
    }
    return (
      state.user.organisations.find((org) => org.id === state.selectedOrganisationId) ||
      state.user.organisations[0] ||
      null
    );
  };

  /**
   * Check if user has multiple organisations
   */
  const hasMultipleOrganisations = (): boolean => {
    return state.user?.organisations ? state.user.organisations.length > 1 : false;
  };

  /**
   * Change password
   */
  const changePassword = async (
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> => {
    if (newPassword !== confirmPassword) {
      throw new Error('New passwords do not match');
    }

    // When the user is in the password-upgrade flow, their Cognito
    // record is the broken side — Cognito's `changePassword` requires
    // an active Cognito session, which they don't have because they
    // were never able to log in via SRP. Skip straight to the Django
    // endpoint, which validates the old password locally, updates
    // Django, and then runs the Cognito sync (admin_set_user_password)
    // with the new compliant password.
    const inUpgradeFlow = !!state.user?.needs_password_upgrade;

    if (config.authMode === 'cognito' && !inUpgradeFlow) {
      // Cognito mode: update Cognito first (SRP — password stays in browser)
      await cognitoChangePass(oldPassword, newPassword);
      // Best-effort Django sync so both systems stay in step.
      // May fail if the Django password is already stale — that's OK,
      // the backend's change_password now also syncs to Cognito.
      try {
        await apiClient.post('/api/users/change-password/', {
          old_password: oldPassword,
          new_password: newPassword,
          new_password_confirm: confirmPassword,
        });
      } catch {
        // Cognito is already updated; Django will catch up on next reset
      }
    } else {
      // Local mode (or upgrade-recovery in cognito mode): the backend
      // endpoint validates against Cognito's policy and then syncs.
      await apiClient.post('/api/users/change-password/', {
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
    }

    // Successful password change clears the upgrade flag on the
    // backend, so reflect that locally too.
    if (state.user?.needs_password_upgrade) {
      const updated: User = { ...state.user, needs_password_upgrade: false };
      saveUserToStorage(updated, state.selectedOrganisationId);
      setState((prev) => ({ ...prev, user: updated }));
    }
  };

  /**
   * Update profile (first name, last name, job title)
   */
  const updateProfile = async (data: {
    firstName: string;
    lastName: string;
    jobTitle: string;
  }): Promise<void> => {
    const response = await apiClient.put<{
      first_name: string;
      last_name: string;
      email: string;
      job_title: string;
    }>('/api/users/profile/', {
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      job_title: (data.jobTitle || '').trim(),
    });

    // Update local user state with new profile data
    if (state.user) {
      const updatedUser: User = {
        ...state.user,
        first_name: response.first_name,
        last_name: response.last_name,
        job_title: response.job_title,
      };

      saveUserToStorage(updatedUser, state.selectedOrganisationId);

      setState((prev) => ({
        ...prev,
        user: updatedUser,
      }));

      emitAuthEvent(AUTH_EVENTS.USER_CHANGED, { userId: updatedUser.id });
    }
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    selectOrganisation,
    refreshUser,
    getSelectedOrganisation,
    hasMultipleOrganisations,
    changePassword,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to require authentication
 * Returns null while loading, redirects or shows login if not authenticated
 */
export function useRequireAuth(): User | null {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  return user;
}

export default AuthContext;
